import type { EpInput, EpResult } from "@nav-games/chartwork";

/// Plots an Estimated Position scenario: the water track (start → DR,
/// one chevron) and the tidal stream (DR → EP, three chevrons). When
/// `revealed` is true it also draws the course made good (start → EP,
/// two chevrons) and the EP symbol, so the answer is only shown after
/// the player has committed.
///
/// North is up. The three points are computed from the same vector
/// maths as `estimatedPosition`, then scaled to fit the viewbox.

const DEG = Math.PI / 180;

const WATER = "#d97706"; // DR / water track — orange (RYA convention)
const TIDE = "#1e6091"; // tidal stream — admiralty blue
const GROUND = "#b8307a"; // course made good — magenta

interface Pt {
  x: number; // east (nm)
  y: number; // north (nm)
}

function chevrons(
  ax: number,
  ay: number,
  bx: number,
  by: number,
  count: number,
  color: string,
) {
  const dx = bx - ax;
  const dy = by - ay;
  const len = Math.hypot(dx, dy) || 1;
  const ux = dx / len;
  const uy = dy / len;
  const px = -uy; // perpendicular
  const py = ux;
  const size = 3.2;
  const gap = 4.2;
  const start = len * 0.5 - ((count - 1) * gap) / 2;
  const marks = [];
  for (let i = 0; i < count; i++) {
    const d = start + i * gap;
    const cx = ax + ux * d;
    const cy = ay + uy * d;
    const apexX = cx + ux * size;
    const apexY = cy + uy * size;
    const w1X = cx + px * size;
    const w1Y = cy + py * size;
    const w2X = cx - px * size;
    const w2Y = cy - py * size;
    marks.push(
      <polyline
        key={i}
        points={`${w1X},${w1Y} ${apexX},${apexY} ${w2X},${w2Y}`}
        fill="none"
        stroke={color}
        strokeWidth={1.4}
        strokeLinecap="round"
        strokeLinejoin="round"
      />,
    );
  }
  return <g>{marks}</g>;
}

export function EpFigure({
  input,
  result,
  revealed,
  size = 300,
}: {
  input: EpInput;
  result: EpResult;
  revealed: boolean;
  size?: number;
}) {
  const run = input.boatSpeedKts * input.hours;
  const drift = input.rateKts * input.hours;

  const start: Pt = { x: 0, y: 0 };
  // The water track follows the (leeway-adjusted) course through the
  // water, not necessarily the heading.
  const dr: Pt = {
    x: run * Math.sin(result.waterTrackDeg * DEG),
    y: run * Math.cos(result.waterTrackDeg * DEG),
  };
  const ep: Pt = { x: result.eastingNm, y: result.northingNm };
  // When leeway has offset the water track from the heading, draw the
  // heading as a faint reference so the leeway angle is visible.
  const leewayDiff =
    ((input.courseSteeredDeg - result.waterTrackDeg + 540) % 360) - 180;
  const hdg: Pt | null =
    Math.abs(leewayDiff) > 0.5
      ? {
          x: run * Math.sin(input.courseSteeredDeg * DEG),
          y: run * Math.cos(input.courseSteeredDeg * DEG),
        }
      : null;

  const pts = hdg ? [start, dr, ep, hdg] : [start, dr, ep];
  const minX = Math.min(...pts.map((p) => p.x));
  const maxX = Math.max(...pts.map((p) => p.x));
  const minY = Math.min(...pts.map((p) => p.y));
  const maxY = Math.max(...pts.map((p) => p.y));
  const pad = 30;
  const rangeX = maxX - minX || 1;
  const rangeY = maxY - minY || 1;
  const s = (size - 2 * pad) / Math.max(rangeX, rangeY);
  // Centre the (usually non-square) bounding box in the viewbox.
  const offX = (size - rangeX * s) / 2;
  const offY = (size - rangeY * s) / 2;

  const project = (p: Pt): [number, number] => [
    offX + (p.x - minX) * s,
    // invert y so north points up
    size - (offY + (p.y - minY) * s),
  ];

  const [sx, sy] = project(start);
  const [dx2, dy2] = project(dr);
  const [ex, ey] = project(ep);
  const hdgProj = hdg ? project(hdg) : null;

  return (
    <svg
      viewBox={`0 0 ${size} ${size}`}
      width={size}
      height={size}
      role="img"
      aria-label="Estimated position plot"
      style={{ maxWidth: "100%", height: "auto", display: "block" }}
    >
      {/* North arrow. */}
      <g transform="translate(18 22)" stroke="var(--text-muted, #64748b)">
        <line x1={0} y1={6} x2={0} y2={-8} strokeWidth={1.2} />
        <polyline points="-3,-4 0,-8 3,-4" fill="none" strokeWidth={1.2} />
        <text x={0} y={20} fontSize={10} textAnchor="middle" fill="var(--text-muted, #64748b)" stroke="none">
          N
        </text>
      </g>

      {/* Heading reference (faint) — only drawn when leeway offsets the
          water track from the heading. */}
      {hdgProj && (
        <>
          <line
            x1={sx}
            y1={sy}
            x2={hdgProj[0]}
            y2={hdgProj[1]}
            stroke="var(--text-muted, #94a3b8)"
            strokeWidth={1.2}
            strokeDasharray="2 3"
          />
          <text
            x={hdgProj[0] + 4}
            y={hdgProj[1]}
            fontSize={9}
            fill="var(--text-muted, #94a3b8)"
          >
            hdg
          </text>
        </>
      )}

      {/* Water track: start → DR (one chevron). */}
      <line x1={sx} y1={sy} x2={dx2} y2={dy2} stroke={WATER} strokeWidth={2} />
      {chevrons(sx, sy, dx2, dy2, 1, WATER)}

      {/* Tidal stream: DR → EP (three chevrons). */}
      <line x1={dx2} y1={dy2} x2={ex} y2={ey} stroke={TIDE} strokeWidth={2} />
      {chevrons(dx2, dy2, ex, ey, 3, TIDE)}

      {/* Course made good: start → EP (two chevrons) — only on reveal. */}
      {revealed && (
        <>
          <line
            x1={sx}
            y1={sy}
            x2={ex}
            y2={ey}
            stroke={GROUND}
            strokeWidth={2}
            strokeDasharray="5 3"
          />
          {chevrons(sx, sy, ex, ey, 2, GROUND)}
        </>
      )}

      {/* Start fix: circle + dot. */}
      <circle cx={sx} cy={sy} r={5} fill="none" stroke="var(--text, #0f172a)" strokeWidth={1.4} />
      <circle cx={sx} cy={sy} r={1.6} fill="var(--text, #0f172a)" />

      {/* DR position: open dot. */}
      <circle cx={dx2} cy={dy2} r={3} fill="var(--bg-card, #fff)" stroke={WATER} strokeWidth={1.4} />

      {/* EP: triangle (only labelled on reveal). */}
      <polygon
        points={`${ex},${ey - 6} ${ex - 5.5},${ey + 4} ${ex + 5.5},${ey + 4}`}
        fill="none"
        stroke={revealed ? GROUND : "var(--text-muted, #94a3b8)"}
        strokeWidth={1.4}
      />
      {revealed && (
        <text x={ex + 9} y={ey + 4} fontSize={11} fontWeight={700} fill={GROUND}>
          EP
        </text>
      )}
    </svg>
  );
}
